import { getContext, setContext, hasContext } from 'svelte';
/**
 * Creates a typesafe context
 */
export class Context {
    #name;
    #key;
    #default;
    /**
     *
     * @param name The name of the context. This is used primarily for debugging purposes.
     */
    constructor(name, defaultValue) {
        this.#name = name;
        this.#key = Symbol(name);
        this.#default = defaultValue;
    }
    /**
     * Returns the key used to set this context.
     *
     * It's recommended not to use this
     */
    get key() {
        return this.#key;
    }
    /**
     * Whether or not the context exists
     *
     * @returns
     */
    exists() {
        return hasContext(this.#key);
    }
    /**
     * Gets the value of the context
     *
     * @throws If the context has not been set
     * @returns
     */
    get() {
        if (!this.exists()) {
            throw new Error(`Context "${this.#name}" not found`);
        }
        return getContext(this.#key);
    }
    /**
     * Gets the current value of the context or returns a fallback value.
     *
     * Note that this does not set the context; it merely returns the fallback value.
     *
     * @param fallback A fallback value to be used in place of the context
     * @returns
     */
    getOr(fallback) {
        if (this.exists()) {
            return getContext(this.#key);
        }
        return fallback;
    }
    /**
     * Gets the current value of the context or returns the default value for the context
     * @returns
     */
    getOrDefault() {
        if (this.exists()) {
            return getContext(this.#key);
        }
        if (this.#default === undefined) {
            throw new Error(`Context "${this.#name}" is not set and does not have a default value`);
        }
        const defaultValue = this.#default();
        return defaultValue;
    }
    /**
     * Gets the current value of the context or sets the context with the default value and returns that.
     *
     * @returns
     */
    getOrSetDefault() {
        if (this.exists()) {
            return getContext(this.#key);
        }
        if (this.#default === undefined) {
            throw new Error(`Context "${this.#name}" is not set and does not have a default value`);
        }
        return this.set(this.#default());
    }
    /**
     * Sets the context with the given value.
     *
     * @param context The initial context
     * @returns
     */
    set(context) {
        return setContext(this.#key, context);
    }
}
